import { type PropsWithChildren, type ReactElement, useMemo, useRef } from 'react'
import { type UserId, isEmpty, isUserId } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { BackendContext, type Stage } from '@front/model'
import { useAuth0 } from '@auth0/auth0-react'

const IMPERSONATING_KEY = 'impersonating'
const clearUrlState = (): void => {
  const url = new URL(window.location.href)
  if (isEmpty(url.search)) return

  url.search = ''
  window.history.replaceState(window.history.state, '', url)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export const WebBackendProvider = ({ children }: PropsWithChildren): ReactElement => {
  const impersonating = useRef<UserId | undefined>(getImpersonatingSession())
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const queryClient = useQueryClient()
  const value = useMemo(
    () => ({
      getStage,
      setStage: () => {
        /*no-op on web, stage is set via url*/
      },
      getImpersonating: () => impersonating.current,
      setImpersonating: (userId?: UserId) => {
        if (userId) window.sessionStorage.setItem(IMPERSONATING_KEY, userId)
        else window.sessionStorage.removeItem(IMPERSONATING_KEY)
        impersonating.current = userId
        clearUrlState()
        queryClient.resetQueries()
      },
      setAuth: () => {
        /*no-op on web, auth is set via Auth0*/
      },
      getToken: getAccessTokenSilently,
      isAuthenticated: () => isAuthenticated,
    }),
    [getAccessTokenSilently, isAuthenticated, queryClient]
  )
  return <BackendContext.Provider value={value}>{children}</BackendContext.Provider>
}

const getImpersonatingSession = (): UserId | undefined => {
  const stored = window.sessionStorage.getItem(IMPERSONATING_KEY)
  if (!stored) return
  if (isUserId(stored)) return stored
}

const stages: Record<string, Stage> = {
  'app.staging.terros.com': 'staging',
  'app.dj.terros.com': 'dj',
}

const getStage = (): Stage => {
  const stageOverride = stages[window.location.hostname]
  if (stageOverride) return stageOverride
  return 'prod'
}
